import { useQueryClient, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { translate } from "shared/languages";
import {
  AccessScope,
  CatalogVisibility,
  ExpiryUnit,
  ProductType,
} from "shared/types";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";

// Body of POST/PUT /v1/product. Promo dates are Jakarta wall dates
// (YYYY-MM-DD); the server stores the day edges.
export interface ProductWriteForm {
  name: string;
  description?: string | null;
  product_type: ProductType;
  class_ids: string[];
  is_promo?: boolean;
  promo_starts_at?: string | null;
  promo_ends_at?: string | null;
  access_scope?: AccessScope | null;
  // Optional: the server defaults to listed.
  client_catalog_visibility?: CatalogVisibility;
  tiers: {
    id?: number;
    total_credits?: number | null;
    price: number;
    expiry_value: number;
    expiry_unit: ExpiryUnit;
  }[];
}

// The catalog product changes what the legacy package lists/pickers see
// (tier names, class links, soft-deletes), so invalidate both namespaces.
const invalidateProductQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: ["product"] });
  queryClient.invalidateQueries({ queryKey: ["package"] });
};

const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (productForm: ProductWriteForm) => {
      return client.post(`/v1/product`, productForm);
    },
    onSuccess: () => {
      invalidateProductQueries(queryClient);
      getApiSuccessMessage(
        `${translate("product")} ${translate("messages.success.create")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      productForm,
    }: {
      productId: string;
      productForm: ProductWriteForm;
    }) => {
      return client.put(`/v1/product/${productId}`, productForm);
    },
    onSuccess: () => {
      invalidateProductQueries(queryClient);
      getApiSuccessMessage(
        `${translate("product")} ${translate("messages.success.update")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (productId: string) => {
      return client.delete(`/v1/product/${productId}`);
    },
    onSuccess: () => {
      invalidateProductQueries(queryClient);
      getApiSuccessMessage(
        `${translate("product")} ${translate("messages.success.delete")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export { useCreateProduct, useUpdateProduct, useDeleteProduct };
